import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined'
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'
import React from 'react'
import Drawer from '../../../components/Drawer'
import GrantContent from '../../../components/GrantContent'
import * as Permisos from '../../../utils/Permisos'
import {
    BITACORA_FILTERS,
    chipClass,
    fiscalLabel,
    formatActivityDate,
    formatWithAge,
    initials,
    labelize,
    NOTE_TYPES,
    noteMeta,
} from '../common/prospect-common'
import { isBusy, useProspectDrawerModel } from './prospect-drawer-model'
import type { ProspectDrawerProps } from './prospect-drawer-model'

export type { ProspectDrawerProps, ProspectDrawerTab } from './prospect-drawer-model'

const actionClass = 'inline-flex h-[38px] items-center gap-1.5 rounded-[10px] border border-k-line-strong bg-transparent px-3 text-[13px] font-bold text-k-ink no-underline hover:bg-k-hover disabled:cursor-not-allowed disabled:opacity-40'

const Field = ({ label, children, wide }: { label: string; children?: React.ReactNode; wide?: boolean }) => (
    <div className={wide ? 'sm:col-span-2' : ''}>
        <dt className="mb-0.5 text-xs text-k-muted">{label}</dt>
        <dd className="m-0 break-words text-sm font-semibold text-k-ink">{children || '—'}</dd>
    </div>
)

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <section className="flex flex-col gap-3 rounded-2xl border border-k-line bg-k-surface px-[18px] py-4">
        <h3 className="m-0 text-[13px] font-extrabold uppercase tracking-[0.06em] text-k-muted">{title}</h3>
        <dl className="m-0 grid grid-cols-1 gap-x-[18px] gap-y-3 sm:grid-cols-2">{children}</dl>
    </section>
)

const Pulse = ({ className }: { className: string }) => <div className={`animate-pulse rounded bg-k-line ${className}`} />

const EmptyState = ({ title, hint }: { title: string; hint?: string }) => (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-k-line bg-k-surface px-4 py-10 text-center">
        <p className="m-0 text-sm font-bold text-k-ink">{title}</p>
        {hint && <p className="mb-0 mt-1 text-xs text-k-muted">{hint}</p>}
    </div>
)

const BitacoraSkeleton = () => (
    <div className="flex flex-col" aria-busy="true" aria-label="Cargando bitácora">
        {[1, 2, 3, 4].map((row) => (
            <div key={row} className="flex gap-3.5">
                <div className="flex flex-col items-center">
                    <Pulse className="h-[34px] w-[34px] rounded-[10px]" />
                    {row < 4 && <span className="my-1 w-0.5 flex-1 bg-k-line" />}
                </div>
                <div className="min-w-0 flex-1 pb-[18px] pt-1">
                    <div className="flex justify-between gap-2">
                        <Pulse className="h-3.5 w-24" />
                        <Pulse className="h-3 w-16" />
                    </div>
                    <Pulse className="mt-2 h-14 w-full rounded-xl" />
                </div>
            </div>
        ))}
    </div>
)

const ProspectDrawer = (props: ProspectDrawerProps) => {
    const { prospectoId, onClose, onEdit } = props
    const model = useProspectDrawerModel(props)
    const { prospecto, loading, name, moral, activo, phone, email, whatsapp } = model

    return (
        <>
            <Drawer open={Boolean(prospectoId)} onClose={onClose} bare width={680}>
                <div className="flex h-full min-h-0 flex-col" role="dialog" aria-modal="true" aria-label={name ? `Detalle de ${name}` : 'Detalle de prospecto'}>
                    <div className="flex shrink-0 flex-col gap-4 border-b border-k-line px-6 pt-5">
                        <div className="flex items-start gap-3.5">
                            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[18px] text-xl font-extrabold text-white" style={{ background: 'linear-gradient(135deg, #1FC8C1, #3E6BE6 55%, #8B3FC9)' }}>
                                {initials(name)}
                            </span>
                            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                                <h2 className="m-0 truncate text-[21px] font-extrabold tracking-[-0.02em] text-k-ink">{name || 'Detalle de prospecto'}</h2>
                                {prospecto && !loading && (
                                    <>
                                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[13px] text-k-muted">
                                            <span>Expediente {prospecto.id}</span>
                                            {model.registroSpoken && <span>Prospecto desde {model.registroSpoken}</span>}
                                        </div>
                                        <div className="flex flex-wrap gap-1.5">
                                            <span className={`rounded-full px-[9px] py-[3px] text-xs font-bold ${moral ? 'bg-tone-purple text-tone-purple-ink' : 'bg-k-accent-soft text-k-accent-ink'}`}>{fiscalLabel(prospecto.fiscal)}</span>
                                            <span className={`rounded-full px-[9px] py-[3px] text-xs font-bold ${activo ? 'bg-k-ok-soft text-k-ok' : 'bg-k-danger-soft text-k-danger-ink'}`}>{labelize(prospecto.status) || '—'}</span>
                                        </div>
                                    </>
                                )}
                            </div>
                            <button type="button" aria-label="Cerrar" className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-k-line bg-transparent text-k-ink hover:bg-k-hover" onClick={onClose}>
                                <CloseIcon style={{ fontSize: 18 }} />
                            </button>
                        </div>

                        {prospecto && !loading && (
                            <div className="flex flex-wrap gap-2">
                                <a className={`${actionClass} ${phone ? '' : 'pointer-events-none opacity-40'}`} href={phone ? `tel:${phone}` : undefined} aria-disabled={!phone}>
                                    <PhoneOutlinedIcon style={{ fontSize: 15 }} />Llamar
                                </a>
                                <a className={`${actionClass} ${whatsapp ? '' : 'pointer-events-none opacity-40'}`} href={whatsapp ? `https://wa.me/${whatsapp}` : undefined} target="_blank" rel="noreferrer" aria-disabled={!whatsapp}>
                                    <ChatOutlinedIcon style={{ fontSize: 15 }} />WhatsApp
                                </a>
                                <a className={`${actionClass} ${email ? '' : 'pointer-events-none opacity-40'}`} href={email ? `mailto:${email}` : undefined} aria-disabled={!email}>
                                    <EmailOutlinedIcon style={{ fontSize: 15 }} />Correo
                                </a>
                                {activo && (
                                    <GrantContent permisos={[Permisos.PROSPECTOS_W]}>
                                        <button type="button" className={actionClass} onClick={() => onEdit?.(prospecto.id)}>
                                            <EditOutlinedIcon style={{ fontSize: 15 }} />Editar
                                        </button>
                                    </GrantContent>
                                )}
                                {activo && (
                                    <GrantContent permisos={[Permisos.PROSPECTOS_D]}>
                                        <button type="button" className={`${actionClass} text-k-danger-ink hover:bg-k-danger-soft`} onClick={model.askDelete} disabled={model.deleting}>
                                            <DeleteOutlinedIcon style={{ fontSize: 15 }} />Eliminar
                                        </button>
                                    </GrantContent>
                                )}
                            </div>
                        )}

                        <div role="tablist" aria-label="Secciones del prospecto" className="-mx-2 flex gap-0.5 overflow-x-auto">
                            {model.tabs.map((item) => {
                                const selected = model.tab === item.id
                                return (
                                    <button
                                        key={item.id}
                                        type="button"
                                        role="tab"
                                        aria-selected={selected}
                                        className={`inline-flex min-h-12 items-center gap-2 whitespace-nowrap border-0 border-b-[3px] bg-transparent px-3 text-sm ${selected ? 'border-k-primary font-extrabold text-k-ink' : 'border-transparent font-semibold text-k-muted'}`}
                                        onClick={() => model.setTab(item.id)}
                                    >
                                        {item.label}
                                        {item.count != null && (
                                            <span className={`inline-flex h-5 min-w-[22px] items-center justify-center rounded-[10px] px-1.5 text-xs font-bold ${selected ? 'bg-k-accent-soft text-k-accent-ink' : 'bg-k-line text-k-ink-2'}`}>
                                                {item.count}
                                            </span>
                                        )}
                                    </button>
                                )
                            })}
                        </div>
                    </div>

                    <div role="tabpanel" className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto bg-k-bg px-6 py-5">
                        {loading && (
                            <div className="flex flex-col items-center justify-center gap-4 py-12">
                                <RefreshIcon className="animate-spin text-k-primary" style={{ fontSize: 40 }} />
                                <p className="text-sm text-k-muted">Cargando detalle...</p>
                            </div>
                        )}

                        {model.error && !loading && (
                            <div className="rounded-lg border border-k-danger/30 bg-k-danger-soft p-4">
                                <div className="flex items-center gap-2">
                                    <ErrorOutlineIcon className="text-k-danger" style={{ fontSize: 20 }} />
                                    <p className="text-sm text-k-danger-ink">{model.error}</p>
                                </div>
                                <Button className="mt-3" variant="contained" onClick={model.reload}>Reintentar</Button>
                            </div>
                        )}

                        {prospecto && !loading && model.tab === 'general' && (
                            <>
                                <Section title="Datos personales">
                                    <Field label="Personalidad">{fiscalLabel(prospecto.fiscal)}</Field>
                                    <Field label="Nombres">{prospecto.nombres}</Field>
                                    <Field label="Apellidos">{model.apellidos}</Field>
                                    <Field label="Fecha de nacimiento">{formatWithAge(prospecto.fecNacimiento)}</Field>
                                    {!moral && <Field label="Sexo">{labelize(prospecto.sexo)}</Field>}
                                    {!moral && <Field label="Estado civil">{labelize(prospecto.edoCivil)}</Field>}
                                </Section>
                                <Section title="Localización">
                                    <Field label="Correo" wide>
                                        {email ? <a href={`mailto:${email}`} className="font-semibold text-k-primary no-underline">{email}</a> : '—'}
                                    </Field>
                                    <Field label="Teléfono">
                                        {phone ? <a href={`tel:${phone}`} className="font-semibold text-k-primary no-underline">{phone}</a> : '—'}
                                    </Field>
                                </Section>
                            </>
                        )}

                        {prospecto && !loading && model.tab === 'bitacora' && (
                            <>
                                <GrantContent permisos={[Permisos.BITACORA_W]}>
                                    <section className="flex flex-col gap-2.5 rounded-2xl border border-k-line bg-k-surface p-3.5">
                                        <div role="radiogroup" aria-label="Tipo de registro" className="flex flex-wrap gap-1.5">
                                            {NOTE_TYPES.map((item) => (
                                                <button key={item.id} type="button" role="radio" aria-checked={model.noteType === item.id} className={chipClass(model.noteType === item.id)} onClick={() => model.setNoteType(item.id)}>
                                                    <item.Icon style={{ fontSize: 16 }} />
                                                    {item.label}
                                                </button>
                                            ))}
                                        </div>
                                        <label className="flex flex-col">
                                            <span className="sr-only">Nuevo registro de bitácora</span>
                                            <textarea rows={3} value={model.note} placeholder={model.notePlaceholder} className="resize-y rounded-xl border border-k-line-strong bg-k-bg px-3 py-2.5 text-sm text-k-ink" onChange={(event) => model.setNote(event.target.value)} />
                                        </label>
                                        <div className="flex flex-wrap items-center justify-between gap-2.5">
                                            <span className="text-xs text-k-muted">Visible para tu equipo · se guarda con fecha</span>
                                            <div className="flex items-center gap-2">
                                                <label className={`inline-flex h-10 cursor-pointer items-center gap-1.5 rounded-[10px] border border-k-line-strong px-3 text-[13px] font-bold text-k-ink ${model.uploading ? 'pointer-events-none opacity-60' : ''}`}>
                                                    <AttachFileOutlinedIcon style={{ fontSize: 16 }} />
                                                    {model.uploading ? 'Subiendo…' : 'Adjuntar'}
                                                    <input type="file" className="sr-only" disabled={model.uploading} onChange={(event) => { model.uploadFile(event.target.files?.[0]); event.target.value = '' }} />
                                                </label>
                                                <button type="button" disabled={!model.note.trim() || model.savingNote} className={`h-10 rounded-[10px] border-0 px-3.5 text-[13px] font-bold ${model.note.trim() && !model.savingNote ? 'cursor-pointer bg-k-primary text-white' : 'cursor-not-allowed bg-k-line text-k-muted-2'}`} onClick={model.addNote}>
                                                    {model.savingNote ? 'Guardando…' : 'Agregar a bitácora'}
                                                </button>
                                            </div>
                                        </div>
                                    </section>
                                </GrantContent>
                                <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filtrar bitácora">
                                    {BITACORA_FILTERS.map((item) => {
                                        const Icon = NOTE_TYPES.find((type) => type.filter === item.id)?.Icon
                                        return (
                                            <button key={item.id} type="button" aria-pressed={model.bitacoraFilter === item.id} className={chipClass(model.bitacoraFilter === item.id)} onClick={() => model.setBitacoraFilter(item.id)}>
                                                {Icon && <Icon style={{ fontSize: 16 }} />}
                                                {item.label}
                                            </button>
                                        )
                                    })}
                                </div>
                                {isBusy(model.bitacoraState) && <BitacoraSkeleton />}
                                {model.bitacoraError && <p className="m-0 text-sm text-k-danger-ink">{model.bitacoraError}</p>}
                                {model.bitacoraState === 'ready' && model.visibleNotes.length === 0 && (
                                    <EmptyState
                                        title="No hay registros"
                                        hint={model.bitacoraFilter === 'todo' ? 'Este prospecto aún no tiene bitácora.' : 'No hay registros en esta categoría.'}
                                    />
                                )}
                                {model.bitacoraState === 'ready' && model.visibleNotes.length > 0 && (
                                    <div className="flex flex-col">
                                        {model.visibleNotes.map((item, index) => {
                                            const meta = noteMeta(item.tipo)
                                            const adjunto = item.adjunto
                                            return (
                                                <div key={item.id || index} className="flex gap-3.5">
                                                    <div className="flex flex-col items-center">
                                                        <span className={`flex h-[34px] w-[34px] items-center justify-center rounded-[10px] ${meta.tone}`}>
                                                            <meta.Icon style={{ fontSize: 16 }} />
                                                        </span>
                                                        {index < model.visibleNotes.length - 1 && <span className="my-1 w-0.5 flex-1 bg-k-line" />}
                                                    </div>
                                                    <div className="flex min-w-0 flex-1 flex-col gap-1 pb-[18px] pt-1">
                                                        <div className="flex flex-wrap justify-between gap-x-2.5 gap-y-1">
                                                            <span className="text-sm font-extrabold">{meta.label}</span>
                                                            <span className="text-xs tabular-nums text-k-muted">{formatActivityDate(item.fecha) || '—'}</span>
                                                        </div>
                                                        {item.texto && <p className="m-0 whitespace-pre-wrap rounded-xl border border-k-line bg-k-surface px-3 py-2.5 text-sm leading-normal text-k-ink-2">{item.texto}</p>}
                                                        {adjunto?.archivo && (
                                                            <button type="button" className="inline-flex w-fit max-w-full items-center gap-1.5 rounded-xl border border-k-line bg-k-surface px-3 py-2 text-left text-sm font-semibold text-k-primary" onClick={() => model.openFile(adjunto.path)}>
                                                                <AttachFileOutlinedIcon style={{ fontSize: 16 }} />
                                                                <span className="truncate">{adjunto.archivo}</span>
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </Drawer>

            <Dialog open={model.confirmDelete} onClose={model.cancelDelete} slotProps={{ paper: { className: 'k-dialog' } }}>
                <DialogTitle>Eliminar prospecto</DialogTitle>
                <DialogContent>
                    <p className="text-sm text-k-ink-2">¿Desea eliminar al prospecto? Si continúa, se perderá toda la información del prospecto, la bitácora y los adjuntos.</p>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={model.cancelDelete}>Cancelar</Button>
                    <Button className="v2-btn v2-btn-danger" variant="contained" onClick={model.deleteProspecto} disabled={model.deleting}>Eliminar</Button>
                </DialogActions>
            </Dialog>

            {model.toast && (
                <div role="status" className="fixed bottom-6 left-6 z-[60] flex items-center gap-2.5 rounded-xl bg-[#1B1D29] px-4 py-3 text-sm text-white shadow-[0_12px_32px_rgba(10,12,20,0.3)]">
                    {model.toast}
                </div>
            )}
        </>
    )
}

export default ProspectDrawer
